'use client'

import { useTranslations } from 'next-intl'

import { trackNewsletterSubscription } from '@/lib/c2c/newsletter/track-subscription'
import { useNewsletterSignup } from '@/lib/c2c/newsletter/use-newsletter-signup'

type TrackFn = typeof trackNewsletterSubscription

interface BlogNewsletterSignupProps {
    locale: string
    /** Base URL for static assets (the Chiku illustration). */
    staticBaseUrl?: string
    /** Injected in tests; defaults to the real tracking pipeline. */
    trackFn?: TrackFn
}

/**
 * The homepage newsletter signup: presentation over the shared
 * `useNewsletterSignup` core, forwarding with the blog's signup source.
 */
export const BlogNewsletterSignup = ({
    locale,
    staticBaseUrl,
    trackFn,
}: BlogNewsletterSignupProps) => {
    const t = useTranslations('blogHome.newsletter')
    const { email, setEmail, status, validationError, isBusy, handleSubmit } = useNewsletterSignup({
        locale,
        subscriptionSource: 'blog_home',
        trackFn,
    })

    return (
        <section aria-label={t('heading')} className="bg-accent px-4 py-8 md:px-10 md:py-12">
            {/* Band background spans full width; content aligns to the page column. */}
            <div className="mx-auto flex w-full max-w-[1400px] flex-wrap items-center justify-between gap-8">
                <div className="flex max-w-xl items-center gap-5">
                    {staticBaseUrl && (
                        // Plain <img>: the static-assets host varies by env
                        // (static.dev.wanderu.dev locally) and only the prod host
                        // is in next/image remotePatterns — same posture as the
                        // C2C and social newsletter forms.
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src={`${staticBaseUrl}static/media/chiku-newsletter.43def637.png`}
                            alt=""
                            aria-hidden="true"
                            width={120}
                            height={104}
                            loading="lazy"
                            className="hidden h-auto w-[120px] shrink-0 md:block"
                        />
                    )}
                    <div>
                        <h2 className="text-xl font-bold text-text-primary md:text-2xl">
                            {t('heading')}
                        </h2>
                        <p className="mt-1 text-[15px] leading-normal text-text-secondary md:text-base">
                            {t('subheading')}
                        </p>
                    </div>
                </div>

                <form
                    className="flex w-full min-w-0 flex-col gap-3 sm:max-w-md sm:flex-1"
                    onSubmit={(e) => void handleSubmit(e)}
                    noValidate
                >
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <input
                            aria-label={t('emailPlaceholder')}
                            aria-invalid={validationError ? 'true' : undefined}
                            className="h-12 w-full flex-1 rounded-lg border border-border bg-background px-3.5 text-base text-text-primary"
                            disabled={isBusy}
                            name="emailAddress"
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('emailPlaceholder')}
                            type="email"
                            value={email}
                        />
                        <button
                            className="h-12 rounded-lg bg-strong px-6 text-base font-bold text-background hover:bg-strong-hover disabled:opacity-50"
                            disabled={isBusy}
                            type="submit"
                        >
                            {t('subscribe')}
                        </button>
                    </div>
                    {validationError && (
                        <p className="text-sm text-destructive" role="alert">
                            {t('invalidEmail')}
                        </p>
                    )}
                    {status === 'success' && (
                        <p className="text-sm font-bold text-text-link">{t('success')}</p>
                    )}
                    {status === 'error' && (
                        <p className="text-sm font-bold text-destructive" role="alert">
                            {t('error')}
                        </p>
                    )}
                </form>
            </div>
        </section>
    )
}
